import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Key, Clock, Gift, Phone, Mail, HelpCircle, AlertTriangle, CreditCard } from "lucide-react";
import { Header, Footer } from "../components/common";
import { BookingModal } from "../components/booking";
import { useBooking } from "../context";
import { Button } from "../components/ui/button";

const HowItWorksPage = () => {
  const { setShowBookingModal } = useBooking();

  const steps = [
    {
      number: 1,
      icon: MessageSquare,
      title: "Booking Confirmation",
      description: "Once booking has been confirmed on our end, we'll send you a message or email to let you know.",
      color: "from-cyan-400 to-teal-500"
    },
    {
      number: 2,
      icon: AlertTriangle,
      title: "Before Your Detailer Arrives",
      description: "Please remove any important valuables from your vehicle before our detailer arrives.",
      note: "Any remaining personal belongings will be placed in a bag and left in your car or returned to you. Please note: We are not liable for any personal items left in your vehicle.",
      color: "from-amber-400 to-orange-500"
    },
    {
      number: 3,
      icon: Key,
      title: "Detailer Arrives",
      description: "Your car detailer will arrive at the booked time and will require your car keys to perform the service.",
      color: "from-teal-400 to-emerald-500"
    },
    {
      number: 4,
      icon: Clock,
      title: "Flexible Key Handover",
      description: "While our detailer is cleaning your car, you may head off and come back later to collect your keys. Won't be back on time? That's alright, you can nominate a designated location for our detailer to leave your keys. We will take a photo of where it was left.",
      note: "Please note: We are not liable for your keys once left in designated location.",
      color: "from-emerald-400 to-green-500"
    },
    {
      number: 5,
      icon: CreditCard,
      title: "Payment",
      description: "If collecting keys in person, payment can be made in cash, EFTPOS or bank transfer. If not at home, make your payment through your account, email or message sent to you!",
      color: "from-blue-400 to-indigo-500"
    },
    {
      number: 6,
      icon: Gift,
      title: "A Little Surprise",
      description: "You'll find a small surprise inside your car as a gift for booking with us!",
      color: "from-purple-400 to-pink-500"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950" data-testid="how-it-works-page">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              How It <span className="gradient-text">Works</span>
            </h1>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Getting your car detailed with RainXpress is simple and hassle-free. Here's what to expect:
            </p>
          </motion.div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="pb-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Vertical Line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-cyan-400 via-emerald-400 to-purple-400 hidden md:block" />
            
            {/* Steps */}
            <div className="space-y-12">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className={`relative flex flex-col md:flex-row items-center gap-6 ${
                    index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                  }`}
                >
                  {/* Step Number Circle */}
                  <div className="absolute left-8 md:left-1/2 transform md:-translate-x-1/2 z-10 hidden md:block">
                    <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center shadow-lg`}>
                      <span className="text-2xl font-bold text-white">{step.number}</span>
                    </div>
                  </div>

                  {/* Content Card */}
                  <div className={`w-full md:w-5/12 ${index % 2 === 0 ? 'md:pr-12' : 'md:pl-12'}`}>
                    <div className="glass rounded-2xl p-6 hover:border-cyan-400/30 transition-all">
                      {/* Mobile Step Number */}
                      <div className={`md:hidden w-12 h-12 rounded-full bg-gradient-to-br ${step.color} flex items-center justify-center mb-4`}>
                        <span className="text-xl font-bold text-white">{step.number}</span>
                      </div>
                      
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${step.color} bg-opacity-20 flex items-center justify-center`}>
                          <step.icon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="text-xl font-bold">{step.title}</h3>
                      </div>
                      
                      <p className="text-slate-300 leading-relaxed">{step.description}</p>
                      
                      {step.subtext && (
                        <p className="text-slate-400 text-sm mt-3 leading-relaxed">{step.subtext}</p>
                      )}
                      
                      {step.note && (
                        <p className="text-amber-400 text-sm mt-3 bg-amber-400/10 p-3 rounded-lg border border-amber-400/20">
                          <strong>⚠️ Note:</strong> {step.note}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Spacer for alternating layout */}
                  <div className="hidden md:block md:w-5/12" />
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Questions Section */}
      <section className="py-16 px-4 md:px-8 bg-slate-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass rounded-2xl p-8 text-center"
          >
            <HelpCircle className="w-12 h-12 text-cyan-400 mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-4">Have Questions?</h2>
            <p className="text-slate-400 mb-6">
              If you have any questions or issues, please do not hesitate to reach out. We're here to help!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="tel:0478054648" 
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-slate-800 hover:bg-slate-700 transition"
              >
                <Phone className="w-5 h-5 text-cyan-400" />
                <span>0478 054 648</span>
              </a>
              <a 
                href="mailto:hello.rainxpress@gmail.com" 
                className="flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-slate-800 hover:bg-slate-700 transition"
              >
                <Mail className="w-5 h-5 text-cyan-400" />
                <span>Email Us</span>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-4">
              Ready to Get <span className="gradient-text">Started?</span>
            </h2>
            <p className="text-slate-400 mb-8">
              Book your detail today and experience the RainXpress difference.
            </p>
            <Button 
              onClick={() => setShowBookingModal(true)}
              className="btn-primary-glow text-slate-900 font-semibold px-8 py-3 rounded-full text-lg"
              data-testid="how-it-works-book-btn"
            >
              Book Now
            </Button>
          </motion.div>
        </div>
      </section>

      <Footer />
      <BookingModal />
    </div>
  );
};

export default HowItWorksPage;
